import debounce from 'lodash/debounce';
import { ReactElement, useCallback, useMemo } from 'react';
import { Control, FieldErrors, FieldValues } from 'react-hook-form';
import { ValueType, type OptionProps, type GroupTypeBase } from 'react-select';

import { useStoreActions, useStoreState } from '~/store/hooks';
import formatClientOptions from '~/utils/formatClientOptions';
import useQuery from '~/store/medicationLogistics/hooks/useQuery';

import Select from '~/ui/components/inputs/SelectWithoutAnimation';
import OptionWithLastRef from '~/ui/components/inputs/SelectWithoutAnimation/components/OptionWithLastRef';
import isClickOnInteractiveElement from '~/ui/components/inputs/SelectWithoutAnimation/optionSelectHelpers';

import { IClientOption } from '~/store/client/types';
import { IOption } from '~/types';
import { IClientTeam } from '~/services/api/clientDetails/types';

interface IClientSelectProps<TFieldValues extends FieldValues = FieldValues> {
  loading: boolean;
  control: Control<TFieldValues>;
  errors: FieldErrors;
  label?: string;
  additionalOption?: IOption;
  loadMore?: () => void;
  onSelect?: (value: IClientOption & { teams?: IClientTeam[] }) => void;
}

const ClientSelect = <TFieldValues extends FieldValues = FieldValues>({
  loading,
  control,
  errors,
  label = 'Select Client',
  additionalOption,
  loadMore,
  onSelect,
}: IClientSelectProps<TFieldValues>): ReactElement => {
  const { list: availableClients, current = [] } = useStoreState(
    store => store.client.clientOptionsByTeam,
  );
  const locationGroups = useStoreState(state => state.clientAllocation.locationGroups);

  const query = useQuery();
  const locationGroupIdQuery = query.get('locationGroupId');

  const locationGroupId = locationGroupIdQuery ? Number(locationGroupIdQuery) : null;

  const { setClientOptionsFilter, setCurrentClientOption } = useStoreActions(
    action => action.client,
  );

  const locationGroupClients = useMemo(
    () => locationGroups.find(group => group.locationGroup.id === locationGroupId)?.clients || [],
    [locationGroups, locationGroupId],
  );

  const allClients = useMemo(
    () => (locationGroupId ? locationGroupClients : [...availableClients, ...current]),
    [locationGroupId, locationGroupClients, availableClients, current],
  );

  const formattedClientOptions = useMemo(() => formatClientOptions(allClients), [allClients]);

  const clientsOptions = useMemo(
    () =>
      additionalOption ? [additionalOption, ...formattedClientOptions] : formattedClientOptions,
    [formattedClientOptions, additionalOption],
  );

  const handleSelectOption = (option: ValueType<IOption | IOption[], boolean>) => {
    const newClient = availableClients.find(
      client => client.id === (option as IOption & { teams?: IClientTeam[] }).value,
    );
    if (newClient) {
      setCurrentClientOption([newClient]);
      onSelect?.(newClient as unknown as IClientOption & { teams?: IClientTeam[] });
    }
  };

  const setValueDebounced = debounce(name => setClientOptionsFilter({ name, pageNumber: 1 }), 500);

  const loadMoreDebounced = loadMore && !loading ? debounce(loadMore, 1000) : undefined;

  const ClientOption = useCallback(
    (props: OptionProps<IOption, boolean, GroupTypeBase<IOption>>) => {
      const { selectOption, data, innerProps } = props;
      const optionDisabled = props.isDisabled;
      const enhancedInnerProps = {
        ...innerProps,
        onMouseDown: (e: React.MouseEvent<HTMLElement>) => {
          const interactive = isClickOnInteractiveElement(e.target);
          if (interactive) e.preventDefault();
          if (!optionDisabled && !interactive) selectOption(data);
          (
            innerProps as { onMouseDown?: (e: React.MouseEvent<HTMLElement>) => void }
          ).onMouseDown?.(e);
        },
        onClick: (e: React.MouseEvent<HTMLElement>) => {
          if (!isClickOnInteractiveElement(e.target)) {
            e.preventDefault();
            e.stopPropagation();
          }
        },
      };
      return (
        <OptionWithLastRef
          {...props}
          innerProps={enhancedInnerProps}
          loadMore={loadMoreDebounced}
        />
      );
    },
    [loadMoreDebounced],
  );

  return (
    <Select
      options={clientsOptions}
      name="client"
      control={control}
      label={label}
      errors={errors}
      hideSelectedOptions={false}
      onInputChange={setValueDebounced}
      customComponents={{
        Option: ClientOption,
      }}
      onSelect={handleSelectOption}
      isLoading={loading}
    />
  );
};

export default ClientSelect;
